import React from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Download, Mail } from 'lucide-react';
import { inferCountry } from '../../../lib/inferCountry';
import { getStatesForCountry } from '../../../lib/geoData';
import { useAltSizePrices } from '../../../hooks/useAltSizePrices';
import { ShippingRateSelector } from '../../../components/ShippingRateSelector';

/**
 * Step 6 — Shipping address, method, and final quote card.
 * The final quote card includes live price, price-per-item/page, the
 * size-adjustment multiplier note, an "Also available at" size ribbon,
 * and the auto-scaled production-size block (edit flow only).
 */
export const Step6Shipping = ({
  shippingAddress,
  setShippingAddress,
  country,
  setCountry,
  // True once the customer has MANUALLY changed the country dropdown —
  // once set, the postal-code inference stops overriding them. Keeps
  // power users' explicit choices sticky while still auto-correcting
  // the common "forgot to flip from USA to Canada" case.
  countryUserSet,
  setCountryUserSet,
  shippingMethod,
  setShippingMethod,
  shippingCosts,
  shippingTimes,
  isShippingAddressComplete,
  subtotal,
  shipping,
  total,
  quantity,
  isIndividualPricing,
  calculateItemsPerPage,
  width,
  height,
  unit,
  sizeMultiplier,
  dimsInInches,
  calculatePrice,
  productionEstimate,
  generatePDF,
  handlePlaceOrder,
  loading,
  placingOrder = false,
  formatCurrency,
  // iter112: callbacks so the "available across" alt-size tiles can
  // re-configure the wizard in-place. Width + height get pushed back
  // up to QuoteCalculator and unit forced to inches so the tile labels
  // (3"×3", 4"×4"…) match the resulting form state.
  selectedProduct,
  selectedFormat,
  selectedMaterial,
  onPickAlternateSize,
  // iter114s.59 — When the customer first clicks an alternate, the
  // wizard captures the prior config as `originalSize`. We re-inject
  // it into the candidate list (if it isn't already one of the
  // standard 2/3/4/5" tiles) so the customer can revert with one
  // click instead of restarting the flow.
  originalSize = null,
  // Quick-quote breakdown — pulled in so the customer sees their full
  // configuration without scrolling back through the wizard.
  formatLabel,
  materialLabel,
  shippingMethodLabel,
  // iter114s.14 — backend combo + tier so the alt-size widget can
  // pre-fetch real catalog prices for every comparison size instead
  // of showing the legacy fallback formula's wrong magnitudes.
  backendCombo = null,
  isEvergreenTier = false,
  // iter114s.18 — live Freightcom rates lifted into QuoteCalculator
  // so we can reflect the real carrier price in the quote total. When
  // `liveRates` has any entries we render the live carrier list +
  // "Live shipping from N carriers" trust badge in place of the static
  // Standard / Rush / Express tiles. The static tiles still render
  // when the address isn't yet complete or Freightcom errors out.
  liveRates = [],
  liveRatesLoading = false,
  liveRatesError = null,
  liveRatesMockMode = false,
  liveRatesPackage = null,
  liveRatesTotalCarriers = 0,
  selectedLiveRate = null,
  setSelectedLiveRate = () => {},
}) => {
  const addressComplete = isShippingAddressComplete();
  const { wIn: curWIn, hIn: curHIn } = dimsInInches();

  // iter114s.39 — Pull the right province/state list for the picked
  // country. Returns null for free-text countries (everything
  // outside US/Canada) so the field falls back to a plain Input.
  const stateOptions = getStatesForCountry(country);

  // Keep `country` and `shippingAddress.stateProvince` in sync so a
  // Canadian "Ontario" pick can't survive a flip to United States.
  // Returning a stale state value through to the Freightcom quote
  // would silently quote a CA→US shipment as US→US (wrong rates).
  const handleCountryChange = (newCountry) => {
    if (newCountry === country) return;
    setCountry(newCountry);
    setCountryUserSet(true);
    const newStates = getStatesForCountry(newCountry);
    const stillValid = newStates ? newStates.includes(shippingAddress.stateProvince) : false;
    if (!stillValid) {
      setShippingAddress({ ...shippingAddress, stateProvince: '' });
    }
  };

  // iter114s.14 — pre-fetch backend prices for the standard 4 alt
  // sizes (2/3/4/5 inches square) so the alt-size widget shows real
  // catalog prices instead of the legacy fallback formula's wrong
  // magnitudes (the customer-flagged "$178.78 actual vs $336.11
  // shown" bug). Caches by (combo, tier) so subsequent visits are
  // free.
  const altPrices = useAltSizePrices({
    combo: backendCombo,
    tier: isEvergreenTier ? 'evergreen' : 'stickeryou',
    enabled: !!backendCombo,
  });

  // iter114s.6 + iter114s.14 — keep ALL 4 size candidates visible
  // even when one matches the current selection. The active candidate
  // renders with a distinct visual treatment + "Current" badge.
  //
  // iter114s.59 — When `originalSize` is set (the customer has clicked
  // at least one alternate during this Step 6 visit), prepend it to
  // the candidate list as the "Original" pill so they can revert with
  // a single click. De-duped against the standard tiles below so
  // "Original 3×3" doesn't render twice.
  const STANDARD_TILES = [
    { label: '2"×2"', wIn: 2, hIn: 2 },
    { label: '3"×3"', wIn: 3, hIn: 3 },
    { label: '4"×4"', wIn: 4, hIn: 4 },
    { label: '5"×5"', wIn: 5, hIn: 5 },
  ];
  const isCurrentFor = (wIn, hIn) =>
    Math.abs(wIn - curWIn) < 0.01 && Math.abs(hIn - curHIn) < 0.01;
  const candidates = [...STANDARD_TILES];
  if (
    originalSize &&
    Number.isFinite(originalSize.wIn) &&
    Number.isFinite(originalSize.hIn) &&
    !STANDARD_TILES.some(
      (t) =>
        Math.abs(t.wIn - originalSize.wIn) < 0.01 &&
        Math.abs(t.hIn - originalSize.hIn) < 0.01,
    )
  ) {
    candidates.unshift({
      label: `${originalSize.wIn}"×${originalSize.hIn}"`,
      wIn: originalSize.wIn,
      hIn: originalSize.hIn,
      isOriginal: true,
    });
  } else if (originalSize) {
    // The original IS one of the standard tiles — just tag it so
    // the UI still surfaces the "Original" badge as guidance.
    const idx = candidates.findIndex(
      (t) =>
        Math.abs(t.wIn - originalSize.wIn) < 0.01 &&
        Math.abs(t.hIn - originalSize.hIn) < 0.01,
    );
    if (idx >= 0) candidates[idx] = { ...candidates[idx], isOriginal: true };
  }
  const sizeCompareCandidates = candidates.map((c) => ({
    ...c,
    isCurrent: isCurrentFor(c.wIn, c.hIn),
  }));

  /**
   * Map each alt-size candidate to a price.
   *
   * iter114s.14 — pulls the REAL backend Excel-sourced price for each
   * (combo, size, qty) combination from the pre-fetched grid. Falls
   * back to the active subtotal only when the backend has no entry
   * for that size (uncommon, e.g., a custom-quote-only finish).
   *
   * Resolves the customer-flagged 3× pricing magnitude bug in the
   * "AVAILABLE ACROSS MULTIPLE CONFIGURATIONS" widget — the alt
   * tiles for Clear BOPP Permanent Glossy at 500qty now show
   * $178.78 / $200.84 / $239.75 / $301.13 (matching the catalog)
   * instead of the legacy formula's $336/504/672/840.
   *
   * Why this works for both INDIVIDUAL and PAGE-mode products:
   *   - Individual: backend has size-specific prices (small stickers
   *     are cheaper than large ones at the same qty).
   *   - Page:       backend has identical prices across sizes for a
   *     given qty (cost-per-sheet is size-independent), so the alt
   *     tiles correctly all match the active subtotal — same end
   *     result as iter114s.6's "alt = subtotal" shortcut, but now
   *     surfaced via the authoritative catalog lookup instead of a
   *     conditional.
   */
  const priceForCandidate = (c) => {
    if (altPrices.status === 'ready') {
      const real = altPrices.lookup(c.wIn, c.hIn, quantity);
      // iter114s.15 — backend returns prices in CAD, but `formatCurrency`
      // expects USD-equivalent values (it multiplies by `currencyRates[CAD]`
      // = 1.37 to display CAD). Same convention as `useQuotePricing.calc`
      // (see hooks/useQuotePricing.js line 89). Without this conversion
      // the alt-size tiles double-applied the FX multiplier and showed
      // every comparison ~37% higher than the actual subtotal (e.g.
      // 5"×5"/250 displayed CAD $274.49 instead of CAD $200.36).
      if (real != null && real > 0) return real / 1.37;
    }
    // Combo not in the catalog OR alt prices still loading → use the
    // active subtotal as a graceful fallback. Customers see a
    // consistent number rather than a wildly-wrong fallback formula.
    return subtotal;
  };

  return (
    <div className="space-y-6">
      {/* Shipping Address */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-[#4A7C59] mb-4">📍 Enter Shipping Address</h3>
        <p className="text-sm text-gray-600 mb-4">
          Please provide your shipping address to calculate accurate shipping rates.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="companyName">Company Name (Optional)</Label>
            <Input
              id="companyName"
              value={shippingAddress.companyName}
              onChange={(e) => setShippingAddress({ ...shippingAddress, companyName: e.target.value })}
              placeholder="Acme Corporation"
            />
          </div>
          <div>
            <Label htmlFor="contactName">Contact Name *</Label>
            <Input
              id="contactName"
              required
              value={shippingAddress.contactName}
              onChange={(e) => setShippingAddress({ ...shippingAddress, contactName: e.target.value })}
              placeholder="John Smith"
              className={!shippingAddress.contactName && 'border-gray-300'}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="addressLine1">Street Address *</Label>
          <Input
            id="addressLine1"
            required
            value={shippingAddress.addressLine1}
            onChange={(e) => setShippingAddress({ ...shippingAddress, addressLine1: e.target.value })}
            placeholder="123 Main Street"
            className={!shippingAddress.addressLine1 && 'border-gray-300'}
          />
        </div>

        <div>
          <Label htmlFor="addressLine2">Apartment, suite, etc. (Optional)</Label>
          <Input
            id="addressLine2"
            value={shippingAddress.addressLine2}
            onChange={(e) => setShippingAddress({ ...shippingAddress, addressLine2: e.target.value })}
            placeholder="Suite 100"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              required
              value={shippingAddress.city}
              onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
              placeholder="New York"
              className={!shippingAddress.city && 'border-gray-300'}
            />
          </div>
          <div>
            <Label htmlFor="stateProvince">State/Province *</Label>
            {stateOptions ? (
              <Select
                value={shippingAddress.stateProvince}
                onValueChange={(v) => {
                  setShippingAddress({ ...shippingAddress, stateProvince: v });
                  // Same auto-flip courtesy as the free-text branch — a
                  // Canadian province pick should infer Country=Canada
                  // unless the customer has already locked it in.
                  if (!countryUserSet) {
                    const inferred = inferCountry(shippingAddress.postalCode, v);
                    if (inferred && inferred !== country) setCountry(inferred);
                  }
                }}
              >
                <SelectTrigger
                  id="stateProvince"
                  data-testid="step6-state-province-select"
                  className={!shippingAddress.stateProvince ? 'border-gray-300' : ''}
                >
                  <SelectValue placeholder={country === 'Canada' ? 'Select a province' : 'Select a state'} />
                </SelectTrigger>
                <SelectContent>
                  {stateOptions.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id="stateProvince"
                required
                value={shippingAddress.stateProvince}
                onChange={(e) => {
                  const stateProvince = e.target.value;
                  setShippingAddress({ ...shippingAddress, stateProvince });
                  // Auto-flip the Country dropdown when the state code
                  // unambiguously identifies a country (e.g., "ON" → Canada,
                  // "CDMX" → Mexico). Silent no-op if the customer has
                  // already hand-picked a country from the dropdown.
                  if (!countryUserSet) {
                    const inferred = inferCountry(
                      shippingAddress.postalCode, stateProvince,
                    );
                    if (inferred && inferred !== country) setCountry(inferred);
                  }
                }}
                placeholder="Region"
                className={!shippingAddress.stateProvince && 'border-gray-300'}
                data-testid="step6-state-province-input"
              />
            )}
          </div>
          <div>
            <Label htmlFor="postalCode">Postal/ZIP Code *</Label>
            <Input
              id="postalCode"
              required
              value={shippingAddress.postalCode}
              onChange={(e) => {
                const postalCode = e.target.value;
                setShippingAddress({ ...shippingAddress, postalCode });
                // Same logic as the state handler — postal patterns are
                // the strongest single signal (CA "A1A 1A1" vs US "12345").
                if (!countryUserSet) {
                  const inferred = inferCountry(
                    postalCode, shippingAddress.stateProvince,
                  );
                  if (inferred && inferred !== country) setCountry(inferred);
                }
              }}
              placeholder="10001"
              className={!shippingAddress.postalCode && 'border-gray-300'}
            />
          </div>
        </div>
      </div>

      {/* Country */}
      <div>
        <Label className="text-lg font-semibold mb-4 block">Country</Label>
        <Select value={country} onValueChange={handleCountryChange}>
          <SelectTrigger data-testid="step6-country-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="United States">United States of America</SelectItem>
            <SelectItem value="Canada">Canada</SelectItem>
            <SelectItem value="Mexico">Mexico</SelectItem>
            <SelectItem value="United Kingdom">United Kingdom</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Shipping Method */}
      {addressComplete ? (
        <div>
          <Label className="text-lg font-semibold mb-4 block">Shipping Method</Label>

          {/* iter114s.18 — Live Freightcom path. When rates have arrived
              (or the loading / error state needs to be surfaced) we
              render the LiveRatesBadge + carrier list via the shared
              ShippingRateSelector component. The customer sees real
              "Canada Post Expedited · 2 days · CAD $14.50" rows
              instead of static "Standard · 6-7 Days · $4.99"
              placeholders. */}
          {/* iter114s.94 — Always route through the live-rates UI.
              The old `(loading || error || rates>0) ? Live : Legacy`
              ternary fell through to the legacy 3-tier
              ($4.99/$19.99/$29.99) whenever the address-aware fetch
              hadn't fired yet (or fired but returned 0 rows) — the
              customer saw misleading hardcoded prices that have
              nothing to do with reality. `ShippingRateSelector`
              already handles empty/loading/error states with its own
              friendly UI; routing everything through it means the
              customer NEVER sees a $4.99 Standard placeholder again,
              especially for US destinations where the legacy ladder
              was wildly off. */}
          <ShippingRateSelector
            rates={liveRates}
            loading={liveRatesLoading}
            error={liveRatesError}
            mockMode={liveRatesMockMode}
            pkg={liveRatesPackage}
            totalCarriers={liveRatesTotalCarriers}
            selectedServiceCode={selectedLiveRate?.service_code}
            onSelect={setSelectedLiveRate}
          />

          <p className="text-sm text-gray-600 mt-2">
            * Dates given are based on after proof approval. Excludes US &amp; Canadian holidays.
          </p>
        </div>
      ) : (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-lg">
          <p className="text-amber-800 font-medium">
            ⚠️ Please complete the shipping address above to view available shipping rates.
          </p>
        </div>
      )}

      {/* Final Quote Card */}
      <div className="bg-gradient-to-br from-[#4A7C59] to-[#6FBF73] text-white p-6 rounded-lg">
        <h3 className="text-2xl font-bold mb-4">Your Quick Quote!</h3>
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-lg">
            <span>Subtotal Cost:</span>
            <span className="font-semibold" data-testid="quote-subtotal">{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between text-lg">
            <span>Shipping &amp; Handling:</span>
            <span className="font-semibold">{formatCurrency(shipping)}</span>
          </div>
          <div className="border-t border-green-300 my-2"></div>
          <div className="flex justify-between text-2xl font-bold">
            <span>Total:</span>
            <span>{formatCurrency(parseFloat(total))}</span>
          </div>
        </div>
        <p className="text-sm text-green-100">(taxes not included)</p>

        {quantity && (
          <div className="mt-4 pt-4 border-t border-green-300">
            <p className="text-sm">
              Your price per {isIndividualPricing ? 'item' : 'page'}:{' '}
              {formatCurrency(subtotal / quantity)}
            </p>
            {/* iter114s.6 — for sticker products, also surface the
                effective per-sticker rate (subtotal / total sticker
                count). The previous "Your price per page" duplicate
                line was using the wrong math (ceil(quantity/itemsPer
                Page)) which produced confusing values like "$27.32
                per page" alongside "$1.64 per page" on the same
                card. */}
            {!isIndividualPricing && width && height &&
              calculateItemsPerPage(width, height) > 0 && (
                <p className="text-sm" data-testid="quote-price-per-sticker">
                  Your price per sticker:{' '}
                  {formatCurrency(
                    subtotal / (quantity * calculateItemsPerPage(width, height)),
                  )}
                </p>
              )}
            {/* iter114s.14 — REMOVED the "Size adjustment: +X% vs.
                2"×2" baseline" footnote. It was a derived multiplier
                from a legacy formula that doesn't match the
                authoritative backend catalog (e.g., Clear BOPP
                Permanent Glossy at 5×5/500qty is $301.13 actual but
                the multiplier copy claimed +150% over a $336.11
                baseline that itself was wrong). The 4-tile size
                comparison ribbon below now shows real catalog
                prices for every size, so the customer can see the
                actual price difference at a glance instead of
                staring at a misleading percentage. */}
          </div>
        )}

        {/* iter112: Order summary so the customer sees their full
            configuration in the Quick Quote panel without scrolling
            back through the wizard. */}
        <div
          className="mt-4 pt-4 border-t border-green-300"
          data-testid="quote-config-summary"
        >
          <p className="text-xs uppercase tracking-wide text-green-100 mb-2">
            Your Configuration
          </p>
          <ul className="text-sm space-y-1">
            {(formatLabel || selectedProduct) && (
              <li className="flex justify-between gap-4">
                <span className="text-green-100">Product</span>
                <span className="font-semibold text-right">
                  {[formatLabel, selectedProduct].filter(Boolean).join(' · ')}
                </span>
              </li>
            )}
            {materialLabel && (
              <li className="flex justify-between gap-4">
                <span className="text-green-100">Material</span>
                <span className="font-semibold text-right">{materialLabel}</span>
              </li>
            )}
            {width && height && (
              <li className="flex justify-between gap-4">
                <span className="text-green-100">Size</span>
                <span className="font-semibold text-right">
                  {width}×{height} {unit || 'inches'}
                </span>
              </li>
            )}
            {quantity && (
              <li className="flex justify-between gap-4">
                <span className="text-green-100">
                  {isIndividualPricing ? 'Quantity' : 'Pages'}
                </span>
                <span className="font-semibold text-right">
                  {quantity}
                  {!isIndividualPricing && calculateItemsPerPage(width, height) > 0 && (
                    <span className="text-green-100 font-normal">
                      {' '}({quantity * calculateItemsPerPage(width, height)} stickers)
                    </span>
                  )}
                </span>
              </li>
            )}
            {shippingMethodLabel && (
              <li className="flex justify-between gap-4">
                <span className="text-green-100">Shipping</span>
                <span className="font-semibold text-right">{shippingMethodLabel}</span>
              </li>
            )}
          </ul>
        </div>

        {quantity && width && height && sizeCompareCandidates.length > 0 && (
          <div
            className="mt-4 pt-4 border-t border-green-300"
            data-testid="quote-size-compare-ribbon"
          >
            <p className="text-xs uppercase tracking-wide text-green-100 mb-1">
              Available Across Multiple Configurations (Same {isIndividualPricing ? 'Quantity' : 'Page Count'})
            </p>
            <p className="text-xs text-green-100/80 mb-2 italic">
              Tap a size to swap your configuration
              {!isIndividualPricing && ' — page count stays the same; total sticker count adjusts to fit the new size'}.
            </p>
            <div className="flex flex-wrap gap-2">
              {sizeCompareCandidates.map((c) => {
                const price = priceForCandidate(c);
                if (price == null) return null;
                const perItem = price / quantity;
                const altIpp = !isIndividualPricing && typeof calculateItemsPerPage === 'function'
                  ? calculateItemsPerPage(c.wIn, c.hIn, 'inch')
                  : null;
                const totalStickers = altIpp ? altIpp * quantity : null;
                const isCurrent = c.isCurrent;
                const isOriginal = c.isOriginal;
                return (
                  <button
                    type="button"
                    key={c.label}
                    onClick={() => !isCurrent && onPickAlternateSize?.(c.wIn, c.hIn)}
                    disabled={isCurrent || !onPickAlternateSize}
                    aria-current={isCurrent ? 'true' : undefined}
                    title={
                      isCurrent
                        ? (isOriginal ? 'This is your original configuration' : 'This is your current size')
                        : isOriginal
                          ? 'Revert to your original size'
                          : altIpp
                            ? `${quantity} pages × ${altIpp} per page = ${totalStickers} total stickers`
                            : `${quantity} stickers @ ${c.label}`
                    }
                    data-testid={`quote-size-compare-${c.wIn}x${c.hIn}`}
                    className={
                      isCurrent
                        ? 'bg-white text-[#4A7C59] border-2 border-white rounded-md px-3 py-2 text-sm flex flex-col items-start gap-0.5 text-left shadow-md animate-pulse-ring cursor-default'
                        : 'bg-white/15 backdrop-blur-sm border border-white/20 rounded-md px-3 py-2 text-sm flex flex-col items-start gap-0.5 text-left transition-all hover:bg-white/25 hover:border-white/40 hover:scale-105 active:scale-95 disabled:hover:bg-white/15 disabled:hover:scale-100 disabled:cursor-default'
                    }
                  >
                    <span className="flex items-center gap-2 w-full">
                      <span className="font-semibold">{c.label}</span>
                      {/* iter114s.59 — Checkmark icon on the active
                          pill (replaces the previous "Current" text
                          badge per operator brief option b). The
                          "Original" tag still surfaces alongside so
                          the customer always knows which one they
                          can revert to. */}
                      {isCurrent && (
                        <span
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#4A7C59] text-white text-[10px] ml-1"
                          aria-label="Current selection"
                          data-testid={`quote-size-current-check-${c.wIn}x${c.hIn}`}
                        >
                          ✓
                        </span>
                      )}
                      {isOriginal && (
                        <span
                          className={`text-[9px] uppercase font-bold tracking-wide rounded px-1.5 py-0.5 ${
                            isCurrent
                              ? 'bg-yellow-100 text-[#4A7C59]'
                              : 'bg-yellow-300 text-[#4A7C59]'
                          }`}
                          data-testid={`quote-size-original-tag-${c.wIn}x${c.hIn}`}
                        >
                          Original
                        </span>
                      )}
                      {!isCurrent && !isOriginal && (
                        <span className="text-green-100">→</span>
                      )}
                      <span className={isCurrent ? 'font-bold ml-auto' : 'font-bold ml-auto'}>
                        {formatCurrency(price)}
                      </span>
                    </span>
                    <span className={isCurrent ? 'text-xs text-[#4A7C59]/80' : 'text-xs text-green-100/90'}>
                      {formatCurrency(perItem)} / {isIndividualPricing ? 'sticker' : 'page'}
                      {totalStickers ? ` · ${totalStickers} stickers` : ''}
                    </span>
                  </button>
                );
              })}
            </div>
            {/* iter114s.14 — explanation for the alt-size widget so the
                customer understands what the comparison shows: same
                product / material / quantity, different physical size.
                Real backend catalog prices are pre-fetched so each
                tile reflects what THAT size would actually cost at
                the customer's current quantity. */}
            <p
              className="mt-3 text-xs italic text-green-100/90"
              data-testid="quote-alt-size-hint"
            >
              Same product, same material, same quantity — different physical size. Each tile shows what THAT size would actually cost at this quantity.
            </p>
            {altPrices.status === 'loading' && (
              <p
                className="mt-1 text-[10px] text-green-100/70"
                data-testid="quote-alt-size-loading"
              >
                Loading comparison prices…
              </p>
            )}
          </div>
        )}

        {productionEstimate?.applicable && productionEstimate?.was_adjusted && (
          <div
            className="mt-4 pt-4 border-t border-green-300"
            data-testid="quote-production-size-block"
          >
            <p className="text-xs uppercase tracking-wide text-green-100 mb-1">
              Production Size (auto-scaled for your artwork)
            </p>
            <div className="flex items-center gap-3 text-sm">
              <span className="text-green-200 line-through" data-testid="quote-production-requested">
                {productionEstimate.requested}
              </span>
              <span>→</span>
              <span className="font-bold text-white text-base" data-testid="quote-production-final">
                {productionEstimate.production}
              </span>
            </div>
            <p className="text-xs text-green-100 mt-1">
              Your artwork&rsquo;s aspect ratio was preserved — we scaled it proportionally to avoid distortion.
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="bg-gradient-to-br from-green-50 to-white border-l-4 border-[#6FBF73] p-6 rounded-lg shadow-md">
        <p className="font-semibold text-[#4A7C59] mb-2 text-lg">Ready to proceed?</p>
        <p className="text-sm text-gray-700 mb-5">Download your quote or place your order below:</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={generatePDF}
            className="bg-white text-[#4A7C59] border-2 border-[#4A7C59] hover:bg-gradient-to-r hover:from-green-50 hover:to-white hover:border-[#6FBF73] flex-1 transition-all duration-300 hover:scale-105 hover:shadow-lg group"
          >
            <Download className="w-5 h-5 mr-2 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-1" />
            Download Quote as PDF
          </Button>
          <Button
            onClick={handlePlaceOrder}
            disabled={loading || placingOrder}
            data-testid="place-order-btn"
            className="bg-gradient-to-r from-[#6FBF73] to-[#4A7C59] hover:from-[#4A7C59] hover:to-[#6FBF73] text-white flex-1 transition-all duration-300 hover:scale-105 hover:shadow-xl group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Mail className="w-5 h-5 mr-2 transition-transform duration-300 group-hover:scale-110" />
            {placingOrder ? 'Generating proof…' : (loading ? 'Loading...' : 'Create PDF Proof')}
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-4 italic" data-testid="create-pdf-proof-disclaimer">
          Create PDF Proof will guide you through uploading artwork and completing your order.
        </p>
        <p className="text-xs text-gray-500 mt-2 italic" data-testid="create-pdf-proof-render-notice">
          Once selected, we will begin generating your live PDF proof… This process may take longer than standard loading times as high-resolution assets are being rendered in real time.
        </p>
      </div>
    </div>
  );
};
